/**
 * `smileBlendshapeSuppressGate` — production 検知経路で ARKit blendshape (口角・頬の上がり) から
 * `suppressFire` flag を組み立てる。 ADR 0004 で記録した「ADR 0002 規律 (transform 層は dev-only)
 * からの時限的逸脱」 の compute 半分。
 *
 * 本 gate の出力 (`suppressFire`) は issue #283 以降 detection-machine.step() で **開口判定 (B 層) の
 * リセット要因** として消費される (= smile の間は `openSince` をリセットし開口継続を打ち切る。 発話
 * `effectiveIsSpeaking` と完全対称)。 本関数自体は smile か否かの boolean を返すだけで、 その
 * 消費セマンティクス (B 層 reset) は detection-machine 側が持つ (= 本ファイルの責務は smile 検出のみ)。
 *
 * 採用根拠 (F24): cycle-2 corpus 評価 (issue #151 P24) で smile-blendshape-v1 の **tight variant**
 * (mouthSmile 0.3 / cheekSquint 0.2) を採用した F24 candidate が smile FP を 16/25 → 4/25 まで削減
 * (corpus/candidates/p24-mar-3d-with-smile-tight.json)。 既定の (loose) 0.4 / 0.3 ではなく tight 値を
 * production canonical として採用する。
 *
 * 拡張根拠 (ADR 0006、 軸 2): `historyFrames` param 追加で直近 N frame の **rolling median** 判定に
 * 拡張可能。 default `historyFrames=1` で既存 per-frame 判定と等価動作 (= default 値による既存挙動
 * の機械的保護、 ADR 0006 §必須条件 2)。 historyFrames>=2 で外れ値 1 件を majority rule で除外し、
 * ARKit blendshape の ML 推定 1 frame 偽陽性 spike による不要な fire 遅延を回避する (= TP retention
 * 保護方向、 真の smile = 連続 signal 高 → median も高で suppress 維持)。
 *
 * Logic (eval substrate との対応):
 *   - historyFrames=1 (default): `src/main/features/eval/lib/transforms/smile-blendshape-v1.ts` L29-46
 *     と同一 (= 既存 v1 等価)
 *   - historyFrames>=2: `src/main/features/eval/lib/transforms/smile-blendshape-rolling-median-v1.ts`
 *     と同一 logic (= ADR 0004 同型 drift mitigation、 中期 R8 で両者 atomic 削除予定 = ADR 0006
 *     §時限化条件)
 *
 * tight 閾値の根拠:
 *   - `MOUTH_SMILE_TIGHT_THRESHOLD = 0.20`: ARKit blendshape 上 完全笑顔 = 1.0、 軽い微笑で 0.3-0.5。
 *     既定 (loose) 0.4 では smile intent の FP を 12/25 残し、 F24 で 0.3 まで下げて corpus 上 4/25 に削減。
 *     issue#276/F47 で vision-VAD を 0.012 に緩めた副作用で smile FP が 1→8 trial に増加 (VAD が口運動経由で
 *     抑えていた笑顔開口が漏れた) → follow-up Step 0 sweep で **0.3→0.20 に締めると yawn-then-open 1.000 /
 *     TP / TP-oblique を一切損なわず smile FP を 8→6 に回収**できると corpus 実証 (§F47.1)。 0.20 は回収が
 *     飽和する手前の安全側 (0.12 まで攻めても 8→5 で限界収益 1 件 + 単一ユーザ corpus への overfit リスク)
 *
 * **cheekSquint 経路の削除 (issue #302)**: 旧実装は `cheekSquintThreshold` (Duchenne marker、 tight 0.10) でも
 * suppress を立てていたが、 Pareto 地図トラック (issue #296 / §F53) の census + ablation で **mouthSmile が
 * 先に suppress を立てるため cheekSquint 単独が決め手になる trial が corpus に存在せず flip 0 件 (= 真の
 * no-op)** と確定したため削除した。 mouthSmile 単独に縮約。 corpus 拡張 (issue #301) 後に cheekSquint が
 * 効きうるかは #301 後の再走で確認する。
 *
 * 残課題 (§F47.1): 締めても残る ~5 件の smile FP は mouthSmile<0.12 = ARKit smile signal がほぼ無い
 * 「笑いながらの口運動」 で、 blendshape gate では原理的に分離不能 (VAD のみが拾えていた)。 別軸 signal の
 * follow-up issue 行き。
 *
 * 中期 (ロードマップ R8) で mouth_width 単位の幾何的意味のある閾値 (= magic threshold を脱する) に
 * 置換される見込み (ADR 0004 §決定 §時限化条件 / ADR 0006 §時限化条件)。
 */

const MOUTH_SMILE_TIGHT_THRESHOLD = 0.2;

function average(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined && b === undefined) return undefined;
  if (a === undefined) return b;
  if (b === undefined) return a;
  return (a + b) / 2;
}

/**
 * 直近 N frame の値群の median (中央値)。 偶数長なら中央 2 つの平均。 空配列は 0 を返す
 * (= caller が空 history で median を取らないよう upstream で guard する想定、 safety net として 0)。
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  // noUncheckedIndexedAccess 配下では index アクセスが `number | undefined` 推論される。
  // length チェック後の確定 access は `!` で escalate。
  if (n % 2 === 1) return sorted[(n - 1) / 2]!;
  return (sorted[n / 2 - 1]! + sorted[n / 2]!) / 2;
}

export function smileBlendshapeSuppressGate(input: {
  /** 現 frame の blendshapes。 historyFrames=1 のとき唯一の判定 source。 */
  blendshapes: Record<string, number> | undefined;
  /**
   * 直近 N frame の blendshape ring buffer (newest last、 current frame を含む想定)。
   * historyFrames>=2 のとき rolling median 計算 source。 length は historyFrames 以下を想定し、
   * 内部で末尾 historyFrames 件にスライスする。 historyFrames=1 のときは無視され `blendshapes`
   * のみ参照する (= 既存 v1 等価)。
   */
  recentBlendshapes?: ReadonlyArray<Record<string, number> | undefined>;
  /**
   * 判定 window size。 default `1` = current frame のみ (= 既存 v1 等価動作)。 `>= 2` で
   * `recentBlendshapes` の末尾 N 件の rolling median 判定 (ADR 0006 §軸 2)。
   */
  historyFrames?: number;
}): boolean {
  const historyFrames = Math.max(1, Math.floor(input.historyFrames ?? 1));

  if (historyFrames === 1) {
    // 既存 v1 等価 path: current frame のみで判定 (= ADR 0006 §必須条件 2 default 等価動作)。
    const bs = input.blendshapes;
    if (bs === undefined) return false;
    const mouthSmile = average(bs.mouthSmileLeft, bs.mouthSmileRight);
    if (mouthSmile !== undefined && mouthSmile >= MOUTH_SMILE_TIGHT_THRESHOLD) return true;
    return false;
  }

  // historyFrames>=2: rolling median 判定。 末尾 N 件を取り、 mouthSmile の median を threshold
  // 比較。 history が短い frame (= 起動直後) では 1 件でも入っていれば median 計算可能 (= length 1 の
  // ときは current 値そのもの = v1 等価挙動に縮退)。
  const window = (input.recentBlendshapes ?? []).slice(-historyFrames);
  if (window.length === 0) return false;

  const mouthSmileValues: number[] = [];
  for (const bs of window) {
    if (bs === undefined) continue;
    const ms = average(bs.mouthSmileLeft, bs.mouthSmileRight);
    if (ms !== undefined) mouthSmileValues.push(ms);
  }

  if (mouthSmileValues.length > 0 && median(mouthSmileValues) >= MOUTH_SMILE_TIGHT_THRESHOLD) return true;
  return false;
}
